import React from "react";
import {
  motion,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from "motion/react";

type ParallaxLayerProps = {
  /** Fraction of scroll distance this layer travels against the content. */
  factor?: number;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
};

/**
 * A fixed backdrop layer that drifts as the page scrolls.
 *
 * Replaces a hand-rolled scroll hook. Motion drives the transform through a
 * motion value, so the position updates outside React's render cycle — the
 * previous version published scroll position as a custom property on :root,
 * which invalidated style for the entire document on every frame and froze the
 * renderer outright.
 *
 * The spring is the reason to use a library here rather than reading scrollY
 * directly: raw scroll offsets track the wheel exactly and read as mechanical,
 * where a lightly damped spring gives the layer a little mass.
 */
const ParallaxLayer: React.FC<ParallaxLayerProps> = ({
  factor = 0.03,
  className = "",
  style,
  children,
}) => {
  const reduce = useReducedMotion();
  const { scrollY } = useScroll();

  // Over a long page the raw range matters less than the rate, so map a fixed
  // window rather than the document height and let it extrapolate.
  const raw = useTransform(scrollY, [0, 1000], [0, -1000 * factor]);
  const y = useSpring(raw, { stiffness: 120, damping: 28, mass: 0.4 });

  return (
    <motion.div
      className={className}
      style={{ ...style, y: reduce ? 0 : y }}
      aria-hidden="true"
    >
      {children}
    </motion.div>
  );
};

export default ParallaxLayer;
